import React, { useState, useEffect } from 'react';
import { 
  CssBaseline, 
  Box, 
  Typography, 
  Input, 
  Button, 
  Alert,
  Card,
  CardContent,
  Sheet
} from '@mui/joy';
// Remove direct import of logo.svg
// import logo from '../logo.svg';
import { verifyBsnr } from '../utils/LicenseVerification';

const BsnrVerification = ({ onVerificationSuccess }) => {
  const [bsnr, setBsnr] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  // Get app name from preloaded settings if available
  const [appName, setAppName] = useState(window.appSettings?.appName || 'QuEry-Tool');
  const logoUrl = '/logo.svg?t=' + Date.now(); // Add cache busting (removed unused setState)
  
  useEffect(() => {
    // Set title with app name
    document.title = `${appName} - BSNR Verification`;
    
    // Only fetch if settings weren't preloaded
    if (!window.appSettings) {
      fetch('/api/appearance/settings')
        .then(response => response.json())
        .then(data => {
          if (data.appName) {
            setAppName(data.appName);
            document.title = `${data.appName} - BSNR Verification`;
            // Store for later use
            window.appSettings = data;
          }
        })
        .catch(error => {
          console.error('Error loading app settings:', error);
        });
    }
  }, [appName]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Clear previous errors
    setError('');
    
    // Validate BSNR format (must be 9 digits)
    if (!/^\d{9}$/.test(bsnr)) {
      setError('Bitte geben Sie eine gültige 9-stellige Betriebsstättennummer ein.');
      return;
    }
    
    setIsLoading(true);
    
    try {
      console.log('Starting authentication for BSNR:', bsnr);
      const isAuthenticated = await verifyBsnr(bsnr);
      
      // Log this login attempt, successful or not
      try {
        await fetch('/api/logs/login', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            bsnr: bsnr,
            success: isAuthenticated
          }),
        });
      } catch (logError) {
        console.error('Failed to log login attempt:', logError);
        // Continue with login process even if logging fails
      }
      
      console.log('Authentication result:', isAuthenticated);
      if (isAuthenticated) {
        console.log('Authentication successful, calling success handler');
        onVerificationSuccess();
      } else {
        console.log('Authentication failed - BSNR not found in license');
        setError('Die eingegebene Betriebsstättennummer ist nicht autorisiert.');
      }
    } catch (err) {
      console.error('Authentication error:', err);
      setError(`Bei der Authentifizierung ist ein Fehler aufgetreten: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <Box 
      sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        minHeight: '100vh',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f5f5f5',
        padding: 2
      }}
    >
      <CssBaseline />
      <Card
        variant="outlined"
        sx={{
          maxWidth: 500,
          width: '100%',
          boxShadow: 'sm',
          borderRadius: 'md'
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>
            <img src={logoUrl} alt="Logo" style={{ height: 60, marginRight: 16 }} />
            <Typography level="h1">{appName}</Typography>
          </Box>
          
          <Sheet
            variant="soft"
            color="neutral"
            sx={{ 
              p: 3, 
              mb: 3, 
              borderRadius: 'md',
              backgroundColor: '#e3f2fd'
            }}
          >
            <Typography level="body1">
              Bitte geben Sie Ihre 9-stellige Betriebsstättennummer (BSNR) ein, um fortzufahren.
            </Typography>
          </Sheet>
          
          {error && (
            <Alert color="danger" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}
          
          <form onSubmit={handleSubmit}>
            <Box sx={{ mb: 3 }}>
              <Input
                fullWidth
                placeholder="Betriebsstättennummer (BSNR)"
                value={bsnr}
                onChange={(e) => setBsnr(e.target.value.replace(/[^\d]/g, ''))}
                slotProps={{
                  input: {
                    maxLength: 9,
                    pattern: '[0-9]*',
                    inputMode: 'numeric'
                  }
                }}
                disabled={isLoading}
              />
            </Box>
            
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
              <Button 
                type="submit" 
                loading={isLoading}
                disabled={bsnr.length !== 9 || isLoading}
              >
                {isLoading ? 'Überprüfe...' : 'Zugang bestätigen'}
              </Button>
            </Box>
          </form>
        </CardContent>
      </Card>
      
      <Typography 
        level="body3" 
        sx={{ mt: 4, color: 'text.tertiary', textAlign: 'center' }}
      >
        Das {appName} ist lizenziert und nur für autorisierte Praxen verfügbar.
        <br />
        Logins werden protokolliert und können bei Missbrauch überprüft werden.
        <br />
        Bei Fragen wenden Sie sich bitte an das QuE Netzbüro.
      </Typography>
    </Box>
  );
};

export default BsnrVerification;
